// packages/web/src/hooks/useServiceDetailAvailability.ts
// Hook for checking service availability via API for service detail page

import { useState, useEffect, useCallback } from 'react';

import { MarketplaceService } from '../types/pricing';
import { ServiceUnavailableData } from '../types/service';

interface UseServiceDetailAvailabilityResult {
  currentServiceUnavailable: ServiceUnavailableData | null;
  checkingAvailability: boolean;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const useServiceDetailAvailability = (
  serviceData: MarketplaceService | undefined,
  selectedAddressId: string | undefined,
  serviceId: string,
): UseServiceDetailAvailabilityResult => {
  const [currentServiceUnavailable, setCurrentServiceUnavailable] =
    useState<ServiceUnavailableData | null>(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  // Check service availability when address changes
  useEffect(() => {
    if (serviceData && selectedAddressId) {
      checkServiceAvailabilityWithAPI(serviceId, selectedAddressId);
    } else if (serviceData && !selectedAddressId) {
      setCurrentServiceUnavailable(null);
    }
  }, [serviceData, selectedAddressId, serviceId]);

  // Check service availability by calling the API with retry logic
  const checkServiceAvailabilityWithAPI = useCallback(
    async (serviceId: string, addressId: string) => {
      if (!serviceId || !addressId) return;

      setCheckingAvailability(true);
      let lastError: Error | null = null;

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          const response = await fetch(`/api/catalog/services/${serviceId}?address=${addressId}`);

          if (response.status === 409) {
            // Service not available
            const errorData = await response.json();
            setCurrentServiceUnavailable({
              service: errorData.service,
              restrictions: errorData.geographicAvailability?.restrictions,
              checkedAddress: errorData.geographicAvailability?.checkedAddress,
            });
            return;
          } else if (response.ok) {
            // Service is available
            setCurrentServiceUnavailable(null);
            return;
          } else {
            throw new Error(`Unexpected response status: ${response.status}`);
          }
        } catch (error) {
          lastError = error as Error;
          if (attempt < MAX_RETRIES) {
            await sleep(RETRY_DELAY * attempt); // Exponential backoff
          }
        }
      }

      // All retries failed
      console.error('Failed to check service availability after retries:', lastError);
      setCurrentServiceUnavailable(null);
      setCheckingAvailability(false);
    },
    [],
  );

  return {
    currentServiceUnavailable,
    checkingAvailability,
  };
};
